/**
 * The ACTIVE COMPANY — the one company this browser tab is working in.
 *
 * A tenant holds several legal entities, and a document keyed under the wrong
 * one posts into the wrong books. The header switcher (CompanySwitcher) sets
 * the company here; `services/api.js` sends it on every request as
 * `X-Company-ID`; the server (apps/core/company_context.py) then:
 *   - offers ONLY this company in every company dropdown (the lock),
 *   - lists this company's GL accounts, ledgers and documents,
 *   - refuses a write that names another company.
 *
 * Per TAB (sessionStorage), so two tabs can work in two companies without one
 * silently changing under the other. A new tab starts from the user's last
 * choice, which the server remembers (and which localStorage mirrors so the
 * very first request of a new tab already carries it).
 *
 * Switching reloads the page: every screen re-reads its data in the new
 * company, and no module-level cache (useReferenceData, useDefaults) survives
 * holding the old one.
 */
import axios from 'axios';
import { useSyncExternalStore } from 'react';

const ENDPOINT = '/financial_accounting/api/active-company/';
const HEADER = 'X-Company-ID';

const host = typeof window !== 'undefined' ? window.location.hostname : '';
const TAB_KEY = `bz.activeCompany.tab.${host}`;
const LAST_KEY = `bz.activeCompany.last.${host}`;

const read = (storage, key) => {
  try { return storage.getItem(key); } catch { return null; }
};
const write = (storage, key, value) => {
  try {
    if (value == null) storage.removeItem(key);
    else storage.setItem(key, String(value));
  } catch { /* private mode / blocked storage: the server still decides */ }
};

let state = {
  status: 'idle',          // idle | loading | ready | error
  companies: [],
  active: null,            // { id, code, name, short_name, currency, currency_symbol }
  defaultSource: null,     // last_used | data_access | first_active
  restricted: false,
  error: null,
};
const listeners = new Set();
const emit = () => listeners.forEach((l) => l());
const setState = (patch) => { state = { ...state, ...patch }; emit(); };

/** The company id this tab sends, or null before one is known. */
export const getActiveCompanyId = () => {
  const raw = read(window.sessionStorage, TAB_KEY) || read(window.localStorage, LAST_KEY);
  const id = raw ? parseInt(raw, 10) : NaN;
  return Number.isFinite(id) ? id : null;
};

/** Adds X-Company-ID to an axios request config (used by every axios instance). */
export const attachCompanyHeader = (config) => {
  const id = getActiveCompanyId();
  if (id && config && config.headers && !config.headers[HEADER]) {
    config.headers[HEADER] = String(id);
  }
  return config;
};

// Raw `axios` (a few screens still import it directly) carries it too.
axios.interceptors.request.use(attachCompanyHeader);

/**
 * Ask the server which companies this user may work in and which one this tab
 * is in. Called once by the layout; the page renders after it settles so no
 * screen's first request goes out without the company.
 */
export const loadActiveCompany = async (http) => {
  if (state.status === 'loading') return state;
  setState({ status: 'loading', error: null });
  try {
    const { data } = await http.get(ENDPOINT);
    const active = data.active || data.default || null;
    // The server's answer wins: a stale or withdrawn company is replaced by
    // the user's default here, before any screen asks for data.
    write(window.sessionStorage, TAB_KEY, active ? active.id : null);
    if (active) write(window.localStorage, LAST_KEY, active.id);
    setState({
      status: 'ready',
      companies: data.companies || [],
      active,
      defaultSource: data.default_source || null,
      restricted: !!data.restricted,
      declaredRejected: data.declared_rejected || null,
    });
  } catch (err) {
    // Never block the app on this: the server still resolves a default for
    // a request that carries no company.
    setState({ status: 'error', error: err?.response?.data?.detail || err.message || 'Failed' });
  }
  return state;
};

/** Switch this tab to `companyId`, remember it as the user's last, and reload. */
export const switchActiveCompany = async (http, companyId) => {
  await http.post(ENDPOINT, { company: companyId });
  write(window.sessionStorage, TAB_KEY, companyId);
  write(window.localStorage, LAST_KEY, companyId);
  window.location.reload();
};

/** Forget this tab's company (logout). The server-side last-used stays, and is
 *  what the next sign-in starts from — the next user must not inherit this one's. */
export const clearActiveCompany = () => {
  write(window.sessionStorage, TAB_KEY, null);
  write(window.localStorage, LAST_KEY, null);
  setState({ status: 'idle', companies: [], active: null });
};

const subscribe = (listener) => { listeners.add(listener); return () => listeners.delete(listener); };
const snapshot = () => state;

/** `{ status, companies, active, defaultSource, restricted, error }` */
export const useActiveCompany = () => useSyncExternalStore(subscribe, snapshot, snapshot);

/** A stable colour per company code, so the header says WHICH books are open at a glance. */
const PALETTE = ['#1565c0', '#2e7d32', '#6a1b9a', '#c62828', '#ef6c00', '#00838f', '#4e342e', '#ad1457'];
export const companyColour = (company) => {
  const key = String(company?.code || company?.id || '');
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
};
